import React from 'react';
import type { FeedbackSendState } from '../../types/feedbackChat';

/**
 * The composer.
 *
 * THE TEXT IS NOT CLEARED ON FAILURE. If a send fails, the member's words stay
 * in the box and a retry appears. Clearing on submit is the conventional
 * pattern and it is wrong here: this is a feedback capture tool, and losing
 * someone's carefully typed report because their train went into a tunnel is
 * the one failure the whole design exists to prevent.
 */

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onRetry: () => void;
  state: FeedbackSendState;
  errorMessage: string | null;
  maxChars: number;
  disabled?: boolean;
  disabledReason?: string | null;
}

export default function FeedbackComposer({
  value, onChange, onSend, onRetry, state, errorMessage, maxChars, disabled, disabledReason,
}: Props) {
  const sending = state === 'sending';
  const failed = state === 'error';
  const remaining = maxChars - value.length;
  const overLimit = remaining < 0;
  const canSend = !disabled && !sending && value.trim().length > 0 && !overLimit;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter is a newline — what people expect from a chat
    // box rather than from a form.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (canSend) onSend();
    }
  };

  if (disabled && disabledReason) {
    return (
      <p
        className="rounded-2xl border px-4 py-3 text-sm leading-relaxed"
        style={{
          borderColor: 'var(--melio-border)',
          backgroundColor: 'var(--melio-cream)',
          color: 'var(--melio-text-muted)',
        }}
      >
        {disabledReason}
      </p>
    );
  }

  return (
    <div>
      {failed && (
        <div
          className="flex items-start justify-between gap-3 pl-3.5 mb-2.5"
          role="alert"
          style={{ borderLeft: '2px solid var(--melio-terracotta-deep)' }}
        >
          <p className="text-[13px] leading-relaxed" style={{ color: 'var(--melio-terracotta-deep)' }}>
            {errorMessage || 'That message could not be sent. Your text has been kept.'}
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="text-[13px] shrink-0 underline underline-offset-4"
            style={{ color: 'var(--melio-terracotta-deep)' }}
          >
            Retry
          </button>
        </div>
      )}

      <div
        className="rounded-3xl border bg-white flex items-end gap-1 p-1.5"
        style={{ borderColor: 'var(--melio-border)' }}
      >
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder="What was confusing, or what would you change?"
          aria-label="Your message"
          className="flex-1 resize-none bg-transparent px-3 py-2 text-sm leading-relaxed outline-none"
          style={{ color: 'var(--melio-ink)', maxHeight: 140 }}
        />
        <button
          type="button"
          onClick={onSend}
          disabled={!canSend}
          aria-label="Send"
          className="rounded-full shrink-0 flex items-center justify-center transition-opacity"
          style={{
            width: 36,
            height: 36,
            backgroundColor: 'var(--melio-ink)',
            color: 'var(--melio-cream-warm)',
            opacity: canSend ? 1 : 0.3,
          }}
        >
          {sending
            ? <span className="text-[11px]">···</span>
            : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 12h15M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
        </button>
      </div>

      {/* Only surfaced as it starts to matter, rather than counting from zero. */}
      {remaining < maxChars * 0.2 && (
        <p
          className="text-[11px] text-right pt-1.5 pr-1"
          style={{ color: overLimit ? 'var(--melio-terracotta-deep)' : 'var(--melio-text-muted)' }}
        >
          {remaining} characters left
        </p>
      )}
    </div>
  );
}
